import React, { useContext } from 'react';

import { makeStyles } from "@mui/styles";

import { IconButton, Tooltip } from '@mui/material';

import { Cancel, Edit, Save, Delete } from '@mui/icons-material';

import { AppContext } from '../../../../RoutesWithAuth';
import { ADMIN_PERMISSIONS } from '../../../common/RolePermissions';
import PermissionButton from '../PermissionButton';

const useStyles = makeStyles(() => ({
    root: {
        display: "flex",
        flex: 1,
        justifyContent: 'space-evenly'
    }
}));
const RowMenuCell = (props) => {
    const {
        api,
        id,
        enableIconCallback,
        customValidation,
        validationColumns = [],
        enableDeleteAction,
        deleteActionCallBack,
        editActionCallBack,
        enableDeleteInSave,
        onRowEditCommit,
        row
    } = props;
    
    const classes = useStyles();
    const context = useContext(AppContext);
    const isAdmin = context?.isAdmin?.get;
    const getPermission = (permission) => typeof permission === 'function' ? permission(row) : permission;

    // Use state to track edit mode so component re-renders when mode changes
    const [isInEditMode, setIsInEditMode] = React.useState(false);
    
    // Update edit mode state when row mode changes
    React.useEffect(() => {
        const checkEditMode = () => {
            const editMode = api.getRowMode(id) === 'edit';
            setIsInEditMode(editMode);
        };
        
        checkEditMode();
        
        // Check periodically or on any API state change
        const interval = setInterval(checkEditMode, 100);
        return () => clearInterval(interval);
    }, [api, id]);

    const handleDeleteClick = (event) => {
        event.stopPropagation();
        event.preventDefault();
        
        // If deleteActionCallBack is not provided, do nothing
        if (!deleteActionCallBack) {
            return;
        }
        
        // For dedicated delete action column, always allow delete
        if (enableDeleteAction) {
            deleteActionCallBack(id, row);
            return;
        }
        
        // For delete button in edit mode, check if editing is allowed
        let editExists = false;
        const allRowIds = api.getAllRowIds();
        allRowIds.forEach((_editId) => {
            if (api.getRowMode(_editId) === 'edit') {
                editExists = true;
            }
        });
        
        if (enableDeleteInSave || !editExists) {
            deleteActionCallBack(id, row);
        }
    }
    const handleEditClick = (event) => {
        event.stopPropagation();
        let editExists = false;
        // Check if any row is in edit mode by checking all rows
        const allRowIds = api.getAllRowIds();
        allRowIds.forEach((_editId) => {
            if (api.getRowMode(_editId) === 'edit') {
                editExists = true;
            }
        });
        if(editActionCallBack && !editExists){
            // Use row prop instead of api.getRow for current data
            editActionCallBack(id, row);
        }else{
            if (!editExists) {
                // Use startRowEditMode instead of setRowMode for MUI DataGrid v6
                if (api.startRowEditMode) {
                    api.startRowEditMode({ id });
                }
            }
        }
    };
    /**
     * 
     * @param {*} row 
     * @returns Boolean Value
     *  true - when all the columns mentioned in validationColumns has value
     *  false - when columns mentioned in validationColumns do not have value
     */
    const isRowValid = (row) => {
        let isValid = true;
        // Check if the columns passed has value 
        validationColumns.forEach((column) => {
            if (!row[column] || !row[column].value || row[column].value?.trim?.() === "") {
                isValid = false;
            }
        })
        // check if any custom validations are enabled using the CustomValidation Prop
        if (isValid && customValidation) {
            isValid = customValidation(row);
        }
        return isValid;
    }
    const handleSaveClick = (event) => {
        event.stopPropagation();
        
        // Try to get the row with updated values from edit state
        let updatedRow = row;
        if (api.getRowWithUpdatedValues) {
            updatedRow = api.getRowWithUpdatedValues(id);
        }
        
        // Build rowEditValues structure for validation
        const rowEditValues = {};
        const columns = api.getAllColumns();
        columns.forEach(col => {
            if (col.editable) {
                rowEditValues[col.field] = { value: updatedRow[col.field] };
            }
        });
        
        if (isRowValid(rowEditValues)) {
            // For existing rows, stop edit mode first to commit the changes
            // For new rows, keep them in edit mode until the server confirms success
            if (!updatedRow.isNewRecord && api.stopRowEditMode) {
                api.stopRowEditMode({ id, ignoreModifications: false });
            }
            
            // Trigger the parent's onRowEditCommit callback
            // The parent will handle refreshing data
            if (onRowEditCommit) {
                onRowEditCommit(updatedRow, {
                    rowInfo: updatedRow,
                    rowId: id,
                    handleCommitCallBack: function (callback_id) {
                        // Use startRowEditMode instead of setRowMode for MUI DataGrid v6
                        if (api.startRowEditMode) {
                            api.startRowEditMode({ id: callback_id });
                        }
                    }
                });
            }
        } else {
            // Trigger the parent's onRowEditCommit callback for invalid row
            if (onRowEditCommit) {
                onRowEditCommit(updatedRow, {
                    invalidRowInfo: updatedRow
                });
            }
        }
    };

    const handleCancelClick = (event) => {
        event.stopPropagation();
        
        // For new records, notify parent to remove the row
        if (row.isNewRecord || row.isNew) {
            // Trigger the parent's onRowEditCommit callback for delete action
            if (onRowEditCommit) {
                onRowEditCommit(row, {
                    newRecord: row,
                    action: 'delete'
                });
            }
        } else {
            // For existing rows, discard changes and stop edit mode
            if (api.stopRowEditMode) {
                api.stopRowEditMode({ id, ignoreModifications: true });
            }
        }
    };
    const DeleteComponent = () => {
        let is_Enabled = true;
        if (enableIconCallback) {
            is_Enabled = enableIconCallback(row);
        }
        if (props?.colDef?.adminEnabled) {
            is_Enabled = isAdmin;
        }
        
        const handleClick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDeleteClick(e);
        };
        
        return (<PermissionButton
            iconOnly
            permission={getPermission(props?.colDef?.adminDeletePermission || props?.colDef?.adminPermission)}
            className={classes.textPrimary}
            size="small"
            //disabled={!is_Enabled}
            aria-label="delete"
            data-testid={"delete"}
            onClick={handleClick}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <Delete fontSize="small" />
        </PermissionButton>)
    }
    if (isInEditMode && !enableDeleteAction) {
        return (
            <div className={classes.root}>
                {enableDeleteInSave && !row.isNewRecord && (<DeleteComponent />)}
                <Tooltip title="Save">
                <PermissionButton
                    iconOnly
                    permission={getPermission(props?.colDef?.adminPermission)}
                    size="small"
                    aria-label="save"
                    data-testid={"save"}
                    onClick={handleSaveClick}
                >
                    <Save fontSize="small" />
                </PermissionButton>
                </Tooltip>
                <Tooltip title="Cancel">
                <IconButton
                    size="small"
                    aria-label="cancel"
                    data-testid={"cancel"}
                    className={classes.textPrimary}
                    onClick={handleCancelClick}
                >
                    <Cancel fontSize="small" color="error" />
                </IconButton>
                </Tooltip>
            </div>
        );
    }

    let editEnabled = true;
    if (props?.colDef?.adminEnabled) {
        editEnabled = isAdmin;
    }
    return (
        <div className={classes.root}>
            {!enableDeleteAction && (<PermissionButton
                iconOnly
                permission={getPermission(props?.colDef?.adminPermission)}
                className={classes.textPrimary}
                size="small"
                aria-label="edit"
                //disabled={!editEnabled}
                data-testid={"edit"}
                onClick={handleEditClick}
            >
                <Edit fontSize="small" />
            </PermissionButton>)}
            {enableDeleteAction && (
                <DeleteComponent />
            )}
        </div>
    );
}
export default RowMenuCell;